import { Navigate, useParams } from 'react-router-dom'
import type { ReportError } from '../api/client'
import { useCarReport } from '../api/useCarReport'
import { ReportVisitContext } from '../api/useReportEngagement'
import { useVehicleDefectProfile } from '../api/useVehicleDefectProfile'
import { useVehicleRarity } from '../api/useVehicleRarity'
import { DocumentMeta } from '../components/DocumentMeta'
import { Icon } from '../components/Icon'
import { LicensePlate } from '../components/LicensePlate'
import { PlateSearch } from '../components/PlateSearch'
import { ReportView } from '../components/ReportView'
import { formatPlate, parsePlate } from '../domain/licensePlate'
import { plateMeta, reportMeta } from '../domain/meta'

export function ReportPage() {
  const { plate: rawPlate = '' } = useParams<{ plate: string }>()
  const parsed = parsePlate(rawPlate)

  // A plate that cannot be a Dutch plate never becomes a request. A plate that is valid but
  // written differently than the canonical form is redirected, so one car has one URL.
  if (!parsed) {
    return <InvalidPlatePage plate={rawPlate} />
  }

  if (parsed.compact !== rawPlate) {
    return <Navigate to={`/kenteken/${parsed.compact}`} replace />
  }

  return <LoadedReportPage plate={parsed.compact} />
}

function LoadedReportPage({ plate }: { plate: string }) {
  const state = useCarReport(plate)

  // Both only once the report is on screen. Each is its own RDW query and neither may sit in front
  // of the report the reader actually asked for — the defect profile least of all, since a cache
  // miss there costs the API a dozen upstream requests and several seconds.
  const loaded = state.status === 'loaded'
  const rarity = useVehicleRarity(plate, loaded)
  const defectProfile = useVehicleDefectProfile(plate, loaded)

  return (
    <main className="container report" id="main">
      {/*
        The metadata follows the state, so a report that is still loading does not sit under the
        finished report's title. Every other state falls back to the plate, which is the only thing
        known at that point and is what somebody searching for it typed.
      */}
      {state.status === 'loaded' ? (
        <DocumentMeta {...reportMeta(state.report)} />
      ) : (
        <DocumentMeta
          {...plateMeta(
            formatPlate(plate),
            `Gratis kentekencheck voor ${formatPlate(plate)}: APK, tellerstand, terugroepacties en techniek.`,
          )}
        />
      )}

      {state.status === 'loading' && <ReportSkeleton plate={plate} />}
      {state.status === 'failed' && <ReportFailure plate={plate} error={state.error} />}
      {state.status === 'loaded' && (
        // The page view the usage events below it are counted under: how far down this report is
        // read, and whether its partner link is clicked.
        <ReportVisitContext.Provider
          value={{ viewId: state.viewId, loadMilliseconds: state.loadMilliseconds }}
        >
          <ReportView report={state.report} rarity={rarity} defectProfile={defectProfile} />
        </ReportVisitContext.Provider>
      )}
    </main>
  )
}

/**
 * The shape of the report, greyed out.
 *
 * The API is fronted by a container that scales to zero, so a first request after an idle period
 * can take 20–30 s. A skeleton that matches the real layout makes that read as loading rather than as
 * nothing happening, and stops the page jumping when the data lands.
 */
function ReportSkeleton({ plate }: { plate: string }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="visually-hidden">Rapport wordt opgehaald…</span>

      <div className="report__header">
        <div style={{ flex: 1 }}>
          <div className="skeleton skeleton-line" style={{ width: '14rem', height: '2rem' }} />
          <LicensePlate value={plate} size="lg" />
        </div>
      </div>

      {[0, 1, 2].map((card) => (
        <div className="card" key={card}>
          <div className="skeleton skeleton-line" style={{ width: '10rem', height: '1.2rem' }} />
          {[0, 1, 2, 3, 4].map((line) => (
            <div className="skeleton skeleton-line" key={line} style={{ width: `${90 - line * 8}%` }} />
          ))}
        </div>
      ))}
    </div>
  )
}

/** Copy per failure kind. Each one tells the user what happened and what they can do next. */
function ReportFailure({ plate, error }: { plate: string; error: ReportError }) {
  const { title, body, canRetry } = describe(error)

  return (
    <div className="state-panel">
      <div className="state-panel__plate">
        <LicensePlate value={plate} size="md" />
      </div>

      <h1>{title}</h1>
      <p>{body}</p>

      {canRetry && (
        <div className="state-panel__actions">
          <button className="button" type="button" onClick={() => window.location.reload()}>
            <Icon name="search" size={16} />
            Probeer opnieuw
          </button>
        </div>
      )}

      <div style={{ marginTop: '2rem' }}>
        <PlateSearch />
      </div>
    </div>
  )
}

function describe(error: ReportError): { title: string; body: string; canRetry: boolean } {
  switch (error.kind) {
    case 'not-found':
      return {
        title: 'Dit kenteken staat niet bij de RDW geregistreerd',
        body: 'Controleer of je het goed hebt overgenomen. Buitenlandse kentekens en voertuigen die uit het register zijn gehaald staan er niet in.',
        canRetry: false,
      }
    case 'invalid-plate':
      return {
        title: 'Dit is geen geldig Nederlands kenteken',
        body: 'Een Nederlands kenteken bestaat uit zes tekens in een vaste combinatie van letters en cijfers, bijvoorbeeld XX-123-X.',
        canRetry: false,
      }
    case 'rate-limited':
      return {
        title: 'Even geduld',
        body: retryHint(
          'Er worden op dit moment te veel kentekens opgevraagd.',
          error.retryAfterSeconds,
        ),
        canRetry: true,
      }
    case 'unavailable':
      return {
        title: 'De RDW is nu niet bereikbaar',
        body: retryHint(
          'De gegevens komen live bij de RDW vandaan en die is op dit moment niet te bereiken. Dit zegt niets over dit voertuig.',
          error.retryAfterSeconds,
        ),
        canRetry: true,
      }
    case 'network':
      return {
        title: 'Geen verbinding',
        body: 'We konden AutoChecken niet bereiken. Controleer je internetverbinding en probeer het opnieuw.',
        canRetry: true,
      }
    default:
      return {
        title: 'Er ging iets mis',
        body: 'Het rapport kon niet worden opgehaald. Probeer het over een moment nog eens.',
        canRetry: true,
      }
  }
}

function retryHint(body: string, retryAfterSeconds?: number): string {
  if (retryAfterSeconds === undefined) {
    return `${body} Probeer het over een moment nog eens.`
  }

  const minutes = Math.ceil(retryAfterSeconds / 60)
  return retryAfterSeconds < 60
    ? `${body} Probeer het over ${retryAfterSeconds} seconden nog eens.`
    : `${body} Probeer het over ${minutes} ${minutes === 1 ? 'minuut' : 'minuten'} nog eens.`
}

function InvalidPlatePage({ plate }: { plate: string }) {
  return (
    <main className="container" id="main">
      <DocumentMeta
        title="Ongeldig kenteken | AutoChecken"
        description="Dit is geen geldig Nederlands kenteken. Voer een kenteken in voor een gratis rapport."
      />

      <div className="state-panel">
        <h1>Dit is geen geldig Nederlands kenteken</h1>
        <p>
          {plate ? <code>{formatPlate(plate)}</code> : 'De ingevoerde waarde'} past op geen enkele
          Nederlandse kentekencombinatie. Probeer het opnieuw.
        </p>
        <div style={{ marginTop: '2rem' }}>
          <PlateSearch autoFocus />
        </div>
      </div>
    </main>
  )
}
